import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface MultipleRingsPlanetGroupProps {
  quaternion?: THREE.Quaternion;
  scale?: number;
  coreCount?: number;
  particlesPerRing?: number;
  numRings?: number;
}

const MultipleRingsPlanetGroup: React.FC<MultipleRingsPlanetGroupProps> = ({
  quaternion,
  scale = 1,
  coreCount = 1200,
  particlesPerRing = 100,
  numRings = 4
}) => {
  const coreParticlesRef = useRef<THREE.Points>(null);
  const ringParticlesRefs = useRef<THREE.Points[]>([]);
  const timeRef = useRef(0);

  const colorScheme = [
    new THREE.Color(0xFF11AA),
    new THREE.Color(0x88DDFF),
  ];

  // Fonction pour générer les positions des particules du noyau
  const generateCoreParticle = (i: number, totalCount: number): THREE.Vector3 => {
    const t = i / totalCount;
    const phi = Math.acos(1 - 2 * t);
    const theta = Math.PI * (1 + Math.sqrt(5)) * i;
    
    const radius = 5 * Math.pow(Math.random(), 2);

    const x = radius * Math.sin(phi) * Math.cos(theta);
    const y = radius * Math.sin(phi) * Math.sin(theta);
    const z = radius * Math.cos(phi);
    return new THREE.Vector3(x, y, z);
  };

  // Fonction pour assigner les propriétés des particules du noyau
  const assignCoreParticleProperties = (
    i: number,
    colors: Float32Array,
    sizes: Float32Array
  ) => {
    const color = colorScheme[i % colorScheme.length];
    const brightness = 1.0 + Math.random() * 0.5;
    sizes[i] = 1 + Math.random() * 1.25;
    
    colors[i * 3] = color.r * brightness;
    colors[i * 3 + 1] = color.g * brightness;
    colors[i * 3 + 2] = color.b * brightness;
  };

  // Fonction pour assigner les propriétés des particules des anneaux
  const assignRingParticleProperties = (
    i: number,
    ringNum: number,
    colors: Float32Array,
    sizes: Float32Array
  ) => {
    const color = colorScheme[ringNum % colorScheme.length];
    const brightness = 0.7 + Math.random() * 0.5;
    sizes[i] = 0.02 + Math.random() * 0.02;
    
    colors[i * 3] = color.r * brightness;
    colors[i * 3 + 1] = color.g * brightness;
    colors[i * 3 + 2] = color.b * brightness;
  };

  // Shader pour les anneaux
  const createRingShaderMaterial = (
    radius: number,
    rotationSpeed: number,
    axis: THREE.Vector3,
    tiltAngle: number,
    perpendicularRotationSpeed: number
  ) => {
    const vertexShader = `
      attribute float size;
      attribute float initialAngle;
      attribute vec3 color;
      
      uniform float uTime;
      uniform float uRadius;
      uniform float uRotationSpeed;
      uniform vec3 uAxis;
      uniform float uTiltAngle;
      uniform float uPerpendicularRotationSpeed;
      
      varying vec3 vColor;
      
      void main() {
        vColor = color;
        
        // Calculer l'angle actuel avec la rotation dans le plan
        float angle = initialAngle + uTime * uRotationSpeed;
        
        // Position initiale dans le plan XY (cercle)
        vec3 pos = vec3(
          cos(angle) * uRadius,
          sin(angle) * uRadius,
          0.0
        );
        
        // Appliquer la rotation d'inclinaison autour de l'axe
        // Rotation autour de l'axe uAxis avec l'angle uTiltAngle
        vec3 axisNormalized = normalize(uAxis);
        float cosTilt = cos(uTiltAngle);
        float sinTilt = sin(uTiltAngle);
        
        // Rotation de Rodrigues pour l'inclinaison
        vec3 rotatedPos = pos * cosTilt + 
                         cross(axisNormalized, pos) * sinTilt + 
                         axisNormalized * dot(axisNormalized, pos) * (1.0 - cosTilt);
        
        // Appliquer la rotation perpendiculaire autour de l'axe Z
        // COMMENTÉ : pour que les anneaux ne tournent que sur eux-mêmes
        // float perpAngle = uTime * uPerpendicularRotationSpeed;
        // float cosPerp = cos(perpAngle);
        // float sinPerp = sin(perpAngle);
        // vec3 finalPos = vec3(
        //   rotatedPos.x * cosPerp - rotatedPos.y * sinPerp,
        //   rotatedPos.x * sinPerp + rotatedPos.y * cosPerp,
        //   rotatedPos.z
        // );
        
        // vec4 mvPosition = modelViewMatrix * vec4(finalPos, 1.0);
        vec4 mvPosition = modelViewMatrix * vec4(rotatedPos, 1.0);
        gl_PointSize = size * (300.0 / -mvPosition.z);
        gl_Position = projectionMatrix * mvPosition;
      }
    `;
    
    const fragmentShader = `
      varying vec3 vColor;
      
      void main() {
        float distance = length(gl_PointCoord - vec2(0.5));
        if (distance > 0.5) discard;
        float alpha = smoothstep(0.5, 0.0, distance);
        gl_FragColor = vec4(vColor, alpha);
      }
    `;
    
    return new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uRadius: { value: radius },
        uRotationSpeed: { value: rotationSpeed },
        uAxis: { value: axis },
        uTiltAngle: { value: tiltAngle },
        uPerpendicularRotationSpeed: { value: perpendicularRotationSpeed },
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
  };

  // Créer la géométrie et les matériaux
  const { coreGeometry, coreMaterial, ringGeometries, ringMaterials } = useMemo(() => {
    // Créer le système de particules pour le noyau
    const coreGeometry = new THREE.BufferGeometry();
    const corePositions = new Float32Array(coreCount * 3);
    const coreColors = new Float32Array(coreCount * 3);
    const coreSizes = new Float32Array(coreCount);
    
    for (let i = 0; i < coreCount; i++) {
      const pos = generateCoreParticle(i, coreCount);
      corePositions[i * 3] = pos.x;
      corePositions[i * 3 + 1] = pos.y;
      corePositions[i * 3 + 2] = pos.z;
      
      assignCoreParticleProperties(i, coreColors, coreSizes);
    }
    
    coreGeometry.setAttribute('position', new THREE.BufferAttribute(corePositions, 3));
    coreGeometry.setAttribute('color', new THREE.BufferAttribute(coreColors, 3));
    coreGeometry.setAttribute('size', new THREE.BufferAttribute(coreSizes, 1));
    
    const coreMaterial = new THREE.ShaderMaterial({
      vertexShader: `
        attribute vec3 color;
        attribute float size;
        varying vec3 vColor;
        uniform float uTime;
        void main() {
          vColor = color;
          vec3 pos = position;

          float unique = (uv.x * uv.y) + position.x + position.y + position.z;
          pos.x += sin(uTime * 0.5 + unique);
          pos.y += cos(uTime * 0.5 + unique);
          pos.z += sin(uTime * 0.5 + unique);

          gl_PointSize = size * 2.5;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        }
      `,
      fragmentShader: `
        varying vec3 vColor;
        void main() {
          float distance = length(gl_PointCoord - vec2(0.5));
          if (distance > 0.5) discard;
          float alpha = smoothstep(0.5, 0.0, distance);
          gl_FragColor = vec4(vColor, alpha);
        }
      `,
      uniforms: {
        uTime: { value: 0.0 }
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });

    // Créer les systèmes de particules pour chaque anneau
    const ringGeometries: THREE.BufferGeometry[] = [];
    const ringMaterials: THREE.ShaderMaterial[] = [];

    for (let ringNum = 0; ringNum < numRings; ringNum++) {
      const ringGeometry = new THREE.BufferGeometry();
      const ringColors = new Float32Array(particlesPerRing * 3);
      const ringSizes = new Float32Array(particlesPerRing);
      const initialAngles = new Float32Array(particlesPerRing);
      // Positions factices (requis par Three.js, mais recalculées dans le shader)
      const ringPositions = new Float32Array(particlesPerRing * 3);
      
      // Stocker les angles initiaux au lieu des positions
      for (let i = 0; i < particlesPerRing; i++) {
        const nodeInRingT = i / particlesPerRing;
        initialAngles[i] = nodeInRingT * Math.PI * 2;
        
        // Positions factices (seront recalculées dans le shader)
        ringPositions[i * 3] = 0;
        ringPositions[i * 3 + 1] = 0;
        ringPositions[i * 3 + 2] = 0;
        
        assignRingParticleProperties(i, ringNum, ringColors, ringSizes);
      }
      
      ringGeometry.setAttribute('position', new THREE.BufferAttribute(ringPositions, 3));
      ringGeometry.setAttribute('color', new THREE.BufferAttribute(ringColors, 3));
      ringGeometry.setAttribute('size', new THREE.BufferAttribute(ringSizes, 1));
      ringGeometry.setAttribute('initialAngle', new THREE.BufferAttribute(initialAngles, 1));
      
      // Paramètres de l'anneau
      const baseRadius = 12 + ringNum * 2;
      const rotationSpeed = 0.2 + ringNum * 0.15;
      const tiltAngle = (ringNum % 2 === 0 ? 1 : -1) * (Math.PI * 2 + ringNum);
      const axisAngle = ringNum * Math.PI / 2;
      const axis = new THREE.Vector3(Math.sin(axisAngle), Math.cos(axisAngle), 0).normalize();
      
      // Vitesse de rotation perpendiculaire (rotation autour de l'axe Z)
      const perpendicularRotationSpeed = 0.1 + ringNum * 0.05;
      
      const ringMaterial = createRingShaderMaterial(
        baseRadius,
        rotationSpeed,
        axis,
        tiltAngle,
        perpendicularRotationSpeed
      );
      
      ringGeometries.push(ringGeometry);
      ringMaterials.push(ringMaterial);
    }

    return {
      coreGeometry,
      coreMaterial,
      ringGeometries,
      ringMaterials
    };
  }, [coreCount, particlesPerRing, numRings]);

  // Animation dans useFrame
  useFrame((state, delta) => {
    timeRef.current += delta;

    if(state)

    // Animer le noyau (rotation GPU)
    if (coreParticlesRef.current) {
      coreMaterial.uniforms.uTime.value = state.clock.elapsedTime;
    }
    
    // Animer chaque anneau - mettre à jour le temps uniform (calculs dans le shader GPU)
    ringParticlesRefs.current.forEach((ring: THREE.Points) => {
      if (ring && ring.material && (ring.material as THREE.ShaderMaterial).uniforms) {
        (ring.material as THREE.ShaderMaterial).uniforms.uTime.value = timeRef.current;
      }
    });
  });

  return (
    <group scale={scale} quaternion={quaternion}>
      {/* Noyau de particules */}
      <points ref={coreParticlesRef} geometry={coreGeometry} material={coreMaterial} frustumCulled={false} />
      
      {/* Anneaux de particules */}
      {ringGeometries.map((ringGeometry, index) => (
        <points
          frustumCulled={false}
          key={index}
          ref={(el) => {
            if (el) {
              if (ringParticlesRefs.current.length <= index) {
                ringParticlesRefs.current.push(el);
              } else {
                ringParticlesRefs.current[index] = el;
              }
            }
          }}
          geometry={ringGeometry}
          material={ringMaterials[index]}
        />
      ))}
    </group>
  );
};

export default MultipleRingsPlanetGroup;

